import { Input } from "@hub/components/ui/input";
import { Textarea } from "@hub/components/ui/textarea";
import { GlossaryEditor, TeamsEditor } from "@hub/ui/OrgListsEditor";
import type { ProfileForm } from "@hub/ui/org-form";
import { profileFromForm } from "@hub/ui/org-form";
import type React from "react";

const TEXT_FIELDS: {
  key: keyof ProfileForm;
  label: string;
  hint?: string;
  multiline?: boolean;
  mono?: boolean;
}[] = [
  { key: "legalName", label: "Legal name" },
  { key: "description", label: "What the company does", multiline: true },
  { key: "industry", label: "Industry" },
  {
    key: "emailDomains",
    label: "Email domains (required)",
    hint: "Comma-separated, e.g. prominent.com, prominent.co.uk",
  },
  { key: "writingStyle", label: "Writing style", multiline: true },
  { key: "primaryColor", label: "Brand primary colour", hint: "#rrggbb" },
  { key: "secondaryColor", label: "Brand secondary colour", hint: "#rrggbb" },
  { key: "logoUrl", label: "Logo URL", hint: "https only; blank for none" },
  { key: "fontFamily", label: "Font family" },
  { key: "documentFooter", label: "Document footer" },
  { key: "supportContact", label: "Support contact email (required)" },
  { key: "assistantName", label: "Assistant name", hint: 'Blank keeps "Helm"' },
];

export function previewParagraph(
  form: ProfileForm,
): { ok: true; text: string } | { ok: false; error: string } {
  const out = profileFromForm(form);
  if (!out.ok) return out;
  const p = out.value;
  const teams = p.teams.map((t) => t.label).join(", ") || "none listed";
  return {
    ok: true,
    text: `${p.name}${p.description ? `: ${p.description}` : ""} Industry: ${p.industry || "not set"}. Teams: ${teams}. Writing style: ${p.writingStyle || "default"}.`,
  };
}

export function OrgProfileForm({
  value,
  onChange,
  section,
}: {
  value: ProfileForm;
  section: "company" | "teams" | "glossary" | "brand";
  onChange: (next: ProfileForm) => void;
}) {
  const preview = previewParagraph(value);
  return (
    <div className="flex flex-col gap-4">
      {section === "company" ? (
        <p className="text-sm text-muted-foreground">
          Tell Helm about the organization. Email domains and support contact are required.
        </p>
      ) : null}
      {section === "brand" ? (
        <p className="text-sm text-muted-foreground">
          Set the assistant’s voice and the branding used in documents.
        </p>
      ) : null}
      {TEXT_FIELDS.filter((f) =>
        section === "company"
          ? ["legalName", "description", "industry", "emailDomains", "supportContact"].includes(
              f.key,
            )
          : section === "brand" &&
            !["legalName", "description", "industry", "emailDomains", "supportContact"].includes(
              f.key,
            ),
      ).map((f) => (
        <div key={f.key} className="text-sm">
          <label htmlFor={`profile-${f.key}`} className="mb-1 block font-medium">
            {f.label}
          </label>
          {f.multiline ? (
            <Textarea
              id={`profile-${f.key}`}
              className={f.mono ? "font-mono text-xs" : undefined}
              value={value[f.key]}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onChange({ ...value, [f.key]: e.target.value })
              }
            />
          ) : (
            <Input
              id={`profile-${f.key}`}
              className="max-w-md"
              value={value[f.key]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...value, [f.key]: e.target.value })
              }
            />
          )}
          {f.hint ? <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p> : null}
        </div>
      ))}
      {section === "teams" ? (
        <TeamsEditor value={value.teams} onChange={(teams) => onChange({ ...value, teams })} />
      ) : null}
      {section === "glossary" ? (
        <GlossaryEditor
          value={value.glossary}
          onChange={(glossary) => onChange({ ...value, glossary })}
        />
      ) : null}
      {section === "company" ? (
        <div className="rounded-lg border border-border bg-muted p-3 text-sm">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What the assistant will be told
          </p>
          {preview.ok ? <p>{preview.text}</p> : <p className="text-destructive">{preview.error}</p>}
        </div>
      ) : null}
    </div>
  );
}
